// Hangul Street Gunsmith (poi `gunsmith`, Okubo east): a Korean-town gun shop painted onto the plain wall behind the
// counter and a crosshair decal on the ground at the counter. Presentation only; painted after the baked lights so it
// stays crisp, so it adds its own soft glows. Called from hd-scene.ts `gameplayMarks`.
import { WORLD, type Rect } from '../../common/src/data';
import { TILE, inTileRect } from '../../common/src/map';
import { type G, K, px, frame, dk, lt, alpha, text, textW } from './hd-kit';

/** 7 wide, 8 tall: the two hangul syllables of 총포 (gunsmith), which the shared sign table doesn't carry */
const HANGUL_GUN: Record<string, string[]> = {
  '총': ['..###..', '...#...', '#######', '...#...', '.#####.', '.#...#.', '.#...#.', '..###..'],
  '포': ['#######', '..#.#..', '..#.#..', '#######', '...#...', '...#...', '...#...', '#######'],
};

function bitmap(g: G, rows: string[], x: number, y: number, col: string, s: number) {
  rows.forEach((r, j) => { for (let i = 0; i < r.length; i++) if (r[i] === '#') px(g, x + i * s, y + j * s, s, s, col); });
}
function glow(g: G, x: number, y: number, rx: number, ry: number, col: string, a: number) {
  g.save(); g.globalCompositeOperation = 'lighter'; g.translate(x, y); g.scale(1, ry / rx);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, rx);
  grd.addColorStop(0, alpha(col, a)); grd.addColorStop(1, alpha(col, 0));
  g.fillStyle = grd; g.fillRect(-rx, -rx, rx * 2, rx * 2); g.restore();
}
/** a rifle side view, 1 unit = s px: stock, receiver, barrel, magazine, optic */
function rifle(g: G, x: number, y: number, s: number, col: string, body: string) {
  const r = (dx: number, dy: number, w: number, h: number, c = col) => px(g, x + dx * s, y + dy * s, w * s, h * s, c);
  r(0, 3, 6, 4, body); r(0, 3, 6, 1); r(6, 2, 10, 4, body); r(6, 2, 10, 1); r(16, 3, 14, 2, body); r(16, 3, 14, 1); // stock, receiver, barrel
  r(29, 2, 2, 4); r(9, 6, 3, 6, body); r(9, 11, 3, 1); r(4, 7, 2, 4, body); r(4, 10, 2, 1); // muzzle, magazine, grip
  r(8, 0, 6, 2, body); r(8, 0, 6, 1); // optic
}

export function paintGunsmith(g: G, R: Rect) {
  const p = WORLD.pois.find(q => q.id === 'gunsmith');
  if (!p || !inTileRect(Math.floor(p.x), Math.floor(p.y), R)) return;
  const ax = Math.round((p.x * TILE - R.x * TILE) * 2), ay = Math.round((p.y * TILE - R.y * TILE) * 2); // the counter, hd px
  const wall = ay - 34; // the wall's foot (the door's threshold)

  // --- the shop window to the left of the door: a workbench scene under a striped awning
  const wx = ax - 190, wy = wall - 48, ww = 124, wh = 46;
  px(g, wx - 2, wy - 2, ww + 4, wh + 4, K.ink);
  px(g, wx, wy, ww, wh, dk(K.amber, 0.78));
  px(g, wx, wy, ww, wh, alpha(K.night, 0.35));
  for (let j = 0; j < 6; j++) px(g, wx, wy + j * 3, ww, 1, alpha(K.amber, 0.12 - j * 0.015)); // lit back wall
  px(g, wx + 4, wy + 6, 38, 2, K.metalLo); px(g, wx + 4, wy + 16, 38, 2, K.metalLo); // parts shelves
  for (let i = 0; i < 6; i++) {
    px(g, wx + 6 + i * 6, wy + 1, 3, 5, i % 2 ? K.metalHi : K.metalLo); // barrels / receivers on the top shelf
    px(g, wx + 6 + i * 6, wy + 10, 4, 6, i % 3 === 0 ? K.pink : K.metal); px(g, wx + 6 + i * 6, wy + 10, 4, 1, K.metalHi); // magazines
  }
  rifle(g, wx + 50, wy + 8, 2, K.metalHi, K.metal); // a rifle on the wall mount
  px(g, wx + 48, wy + 7, 2, 12, K.metalLo); px(g, wx + 112, wy + 7, 2, 12, K.metalLo);
  px(g, wx + 2, wy + 30, ww - 4, 4, K.wood); px(g, wx + 2, wy + 30, ww - 4, 1, K.woodHi); px(g, wx + 2, wy + 34, ww - 4, 3, K.woodLo); // workbench
  px(g, wx + 8, wy + 22, 14, 8, K.metalLo); px(g, wx + 8, wy + 22, 14, 2, K.metalHi); px(g, wx + 20, wy + 24, 5, 3, K.metalHi); px(g, wx + 12, wy + 30, 6, 1, K.ink); // vice
  px(g, wx + 40, wy + 28, 20, 2, K.metalHi); px(g, wx + 62, wy + 27, 8, 3, K.amber); px(g, wx + 74, wy + 26, 3, 4, K.metalHi); // part in progress, ammo, screwdriver
  px(g, wx + 86, wy + 22, 10, 8, K.ink); px(g, wx + 87, wy + 23, 8, 6, K.cyan); px(g, wx + 88, wy + 24, 6, 1, K.white); // bench lamp console
  px(g, wx + ww - 18, wy + 26, 14, 4, K.metalLo); px(g, wx + ww - 16, wy + 24, 8, 2, K.metalHi); // cleaning kit
  px(g, wx, wy + 37, ww, 9, alpha(K.night, 0.5)); // apron below the bench in shadow
  for (let i = 0; i < ww; i += 31) px(g, wx + i, wy, 1, wh, alpha(K.night, 0.6)); // window bars
  frame(g, wx - 2, wy - 2, ww + 4, wh + 4, K.metal);
  // awning (pink/ink stripes)
  const aw = ww + 12, ax0 = wx - 6, ay0 = wy - 12;
  for (let i = 0; i < aw; i += 12) { px(g, ax0 + i, ay0, Math.min(6, aw - i), 9, K.red); px(g, ax0 + i + 6, ay0, Math.min(6, aw - i - 6), 9, K.ink); }
  px(g, ax0, ay0, aw, 2, alpha(K.white, 0.18));
  for (let i = 0; i < aw; i += 6) px(g, ax0 + i + 1, ay0 + 9, 4, 2, (i / 6) % 2 ? K.ink : K.red);
  px(g, ax0, ay0 + 11, aw, 3, alpha(K.night, 0.45));
  glow(g, wx + ww / 2, wy + 30, 80, 30, K.amber, 0.28);

  // --- neon rifle and GUNSMITH plate above the door
  const dx = ax - 54, dy = wall - 74; // the door's top left
  px(g, dx - 34, dy - 22, 88, 18, K.ink); px(g, dx - 33, dy - 21, 86, 16, dk(K.cyan, 0.82)); frame(g, dx - 31, dy - 19, 82, 12, K.cyan); frame(g, dx - 30, dy - 18, 80, 10, dk(K.cyan, 0.4));
  rifle(g, dx - 27, dy - 18, 1, lt(K.cyan, 0.6), lt(K.cyan, 0.3));
  glow(g, dx + 10, dy - 14, 66, 22, K.cyan, 0.45);
  text(g, 'GUNSMITH', dx + 6, dy - 17, lt(K.cyan, 0.7), 1);

  // --- vertical neon blade sign 총포, right of the door
  const bx = ax + 10, by = wall - 112, bw = 32, bh = 50;
  px(g, bx - 3, by + 6, 3, 2, K.metalLo); px(g, bx - 3, by + bh - 8, 3, 2, K.metalLo);
  px(g, bx, by, bw, bh, K.ink); px(g, bx + 1, by + 1, bw - 2, bh - 2, dk(K.pink, 0.8)); frame(g, bx + 2, by + 2, bw - 4, bh - 4, K.pink); frame(g, bx + 3, by + 3, bw - 6, bh - 6, dk(K.pink, 0.4));
  bitmap(g, HANGUL_GUN['총'], bx + 4, by + 5, lt(K.pink, 0.6), 3);
  bitmap(g, HANGUL_GUN['포'], bx + 4, by + 5 + 8 * 3 - 1, lt(K.pink, 0.6), 3);
  glow(g, bx + bw / 2, by + bh / 2, 46, 52, K.pink, 0.5);

  // --- crosshair decal on the pavement at the counter, inside the dashed reach square
  const cx = ax, cy = ay;
  for (let a = 0; a < 360; a += 2) {
    const rad = a * Math.PI / 180, c = Math.cos(rad), s = Math.sin(rad);
    px(g, Math.round(cx + c * 20), Math.round(cy + s * 20 * 0.8), 2, 2, alpha(K.pink, 0.55));
    if (a % 4 === 0) px(g, Math.round(cx + c * 14), Math.round(cy + s * 14 * 0.8), 1, 1, alpha(K.pink, 0.3));
  }
  for (const [ox, oy, w, h] of [[-26, -1, 12, 2], [14, -1, 12, 2], [-1, -24, 2, 10], [-1, 12, 2, 10]]) px(g, cx + ox, cy + oy, w, h, alpha(K.pink, 0.6));
  px(g, cx - 1, cy - 1, 3, 3, alpha(K.white, 0.5));
  glow(g, cx, cy, 36, 28, K.pink, 0.16);
}
